import { Component, Prop, State, Element, Event, EventEmitter, Watch, h, Build, Host } from '@stencil/core';
import { debounce } from '../../../../../utils/helpers/debounce';

/**
 * Navbar search component for SignNow brand header search
 */

// Inline search icon for LCP optimization
const SearchIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="sn-navbar-search__find" aria-hidden="true">
    <path
      d="M19.023 16.977C18.5546 16.5284 18.0988 16.0669 17.656 15.593C17.284 15.215 17.06 14.94 17.06 14.94L14.26 13.603C15.381 12.3316 15.9997 10.695 16 9C16 5.141 12.86 2 9 2C5.14 2 2 5.141 2 9C2 12.859 5.14 16 9 16C10.763 16 12.37 15.34 13.603 14.261L14.94 17.061C14.94 17.061 15.215 17.285 15.593 17.657C15.98 18.02 16.489 18.511 16.977 19.024L18.335 20.416L18.939 21.062L21.06 18.941L20.414 18.337C20.035 17.965 19.529 17.471 19.023 16.977ZM9 14C6.243 14 4 11.757 4 9C4 6.243 6.243 4 9 4C11.757 4 14 6.243 14 9C14 11.757 11.757 14 9 14Z"
      fill="currentColor"
    />
  </svg>
);

// Inline close icon for LCP optimization
const CloseIcon = ({ onClick }: { onClick?: (e: any) => void }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="sn-navbar-search__clean" aria-hidden="true" onClick={onClick}>
    <path
      d="M16.192 6.34424L11.949 10.5862L7.70697 6.34424L6.29297 7.75824L10.535 12.0002L6.29297 16.2422L7.70697 17.6562L11.949 13.4142L16.192 17.6562L17.606 16.2422L13.364 12.0002L17.606 7.75824L16.192 6.34424Z"
      fill="currentColor"
    />
  </svg>
);
/**
 * Navbar search component for SignNow brand header search.
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 * @contentConfig Desktop search component for navbar with suggestions and controls
 * @example
 *   <sn-navbar-search-type-1
 *     placeholder="Find anything (ie. integration, solutions, etc.)"
 *     searchLabel="Search"
 *     startSearchingLabel="to start searching"
 *     searchErrorLabel="This field may not be empty."
 *     suggestions='["Edit PDFs online", "Send documents for signing", "Create reusable templates"]'
 *   ></sn-navbar-search-type-1>
 */
@Component({
  tag: 'sn-navbar-search-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNNavbarSearch {
  @Element() el!: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  private inputElement?: HTMLInputElement;
  private searchWrapperElement?: HTMLElement;
  private focusTimeout?: number;
  private blurTimeout?: number;
  private resizeObserver?: ResizeObserver;
  private debouncedHandleResize?: () => void;

  /**
   * Controls search visibility from parent component
   */
  @Prop({ mutable: true }) opened: boolean = false;

  /**
   * Placeholder text for search input
   */
  /**
   * Placeholder text for search input
   */
  @Prop() placeholder: string;

  /**
   * Array of search suggestions (strings or {label, href} objects)
   * @dynamic
   * @type {array}
   */
  @Prop() suggestions: any[] = [];

  /**
   * Label for search input (accessibility)
   */
  @Prop() searchLabel: string;

  /**
   * Text for "to start searching" helper
   */
  @Prop() startSearchingLabel: string;

  /**
   * Error message for empty search
   */
  @Prop() searchErrorLabel: string;

  /**
   * Internal visibility state
   */
  @State() isVisible: boolean = false;

  /**
   * Current search input value
   */
  @State() searchValue: string = '';

  /**
   * Show/hide suggestions list
   */
  @State() showSuggestions: boolean = false;

  /**
   * Show/hide search controls
   */
  @State() showControls: boolean = false;

  /**
   * Validation error state
   */
  @State() hasError: boolean = false;

  /**
   * Selected suggestion index for keyboard navigation
   */
  @State() selectedSuggestionIndex: number = -1;

  private suggestionsListElement?: HTMLUListElement;
  private isProcessingKeyDown: boolean = false;

  @Watch('showSuggestions')
  handleShowSuggestionsChange(newValue: boolean) {
    if (newValue) {
      // Ensure element has initial hidden state before clearing inline styles
      if (this.suggestionsListElement) {
        // Set initial state first
        this.suggestionsListElement.style.opacity = '0';
        this.suggestionsListElement.style.visibility = 'hidden';
        this.suggestionsListElement.style.pointerEvents = 'none';
        this.suggestionsListElement.style.transform = 'translateY(-10px)';

        // Use double requestAnimationFrame to ensure browser applies initial state before clearing
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.suggestionsListElement && this.showSuggestions) {
              this.suggestionsListElement.style.opacity = '';
              this.suggestionsListElement.style.visibility = '';
              this.suggestionsListElement.style.pointerEvents = '';
              this.suggestionsListElement.style.transform = '';
            }
          });
        });
      }
    } else {
      // Force immediate hiding with inline styles
      if (this.suggestionsListElement) {
        this.suggestionsListElement.style.opacity = '0';
        this.suggestionsListElement.style.visibility = 'hidden';
        this.suggestionsListElement.style.pointerEvents = 'none';
        this.suggestionsListElement.style.transform = 'translateY(-10px)';
      }
    }
  }

  /**
   * Emitted when search visibility changes
   */
  @Event() snSearchOpenChange!: EventEmitter<{ opened: boolean }>;

  /**
   * Emitted when input value changes
   */
  @Event() snSearchInputChange!: EventEmitter<{ value: string }>;

  /**
   * Emitted on search submit (Enter key)
   */
  @Event() snSearchSubmit!: EventEmitter<{ value: string }>;

  /**
   * Emitted when clear button is clicked
   */
  @Event() snSearchClear!: EventEmitter<Record<string, never>>;

  /**
   * Watch for changes to opened prop from parent
   */
  @Watch('opened')
  handleOpenedChange(newValue: boolean) {
    if (newValue !== this.isVisible) {
      this.isVisible = newValue;
      this.updateSearchVisibilityClass();

      // Handle focus management when opened changes
      if (Build.isBrowser && window.innerWidth > 960) {
        if (newValue) {
          // Focus after opening
          if (this.focusTimeout) clearTimeout(this.focusTimeout);
          this.focusTimeout = window.setTimeout(() => {
            this.inputElement?.focus();
          }, 80);
        } else {
          // Blur after closing
          if (this.blurTimeout) clearTimeout(this.blurTimeout);
          this.blurTimeout = window.setTimeout(() => {
            this.inputElement?.blur();
          }, 80);
        }
      }
    }
  }

  /**
   * Update body class based on search visibility (desktop only)
   * Similar to navbar-fixed class management
   */
  private updateSearchVisibilityClass() {
    if (!Build.isBrowser || window.innerWidth <= 960) return;

    if (this.isVisible) {
      document.body.classList.add('search-visible');
    } else {
      document.body.classList.remove('search-visible');
    }
  }

  componentDidLoad() {
    // Sync with prop
    this.isVisible = this.opened;
    this.updateSearchVisibilityClass();

    // Attach document click listener for hiding suggestions
    document.addEventListener('click', this.handleDocumentClick);

    // Initialize ARIA attributes
    this.updateAriaAttributes();

    // Setup position tracking
    this.setupPositionTracking();
  }

  disconnectedCallback() {
    // Clean up event listeners and timeouts
    document.removeEventListener('click', this.handleDocumentClick);
    // Remove body class on cleanup
    if (Build.isBrowser) {
      document.body.classList.remove('search-visible');
    }
    if (this.focusTimeout) clearTimeout(this.focusTimeout);
    if (this.blurTimeout) clearTimeout(this.blurTimeout);

    // Clean up position tracking
    this.cleanupPositionTracking();
  }

  /**
   * Setup position tracking for suggestions list
   */
  private setupPositionTracking = () => {
    if (!Build.isBrowser) return;
    // Create debounced version of handleWindowResize
    this.debouncedHandleResize = debounce(this.handleWindowResize, 100);

    // Track window resize
    window.addEventListener('resize', this.debouncedHandleResize);
    window.addEventListener('scroll', this.debouncedHandleResize, true);

    // Track parent resize
    if (window.ResizeObserver && this.searchWrapperElement) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.debouncedHandleResize) {
          this.debouncedHandleResize();
        }
      });
      this.resizeObserver.observe(this.searchWrapperElement);
    }
  };

  /**
   * Cleanup position tracking
   */
  private cleanupPositionTracking = () => {
    if (!Build.isBrowser) return;
    if (this.debouncedHandleResize) {
      window.removeEventListener('resize', this.debouncedHandleResize);
      window.removeEventListener('scroll', this.debouncedHandleResize, true);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  };

  /**
   * Handle window resize
   */
  private handleWindowResize = () => {
    // Update max-height of suggestions list if needed
    if (this.suggestionsListElement && this.showSuggestions) {
      const filteredSuggestions = this.getFilteredSuggestions();
      const MAX_DROPDOWN_HEIGHT = 240;
      const estimatedHeight = Math.min(filteredSuggestions.length * 44 + 16, MAX_DROPDOWN_HEIGHT);
      this.suggestionsListElement.style.maxHeight = `${estimatedHeight}px`;
    }
  };

  /**
   * Toggle search visibility (desktop only)
   */
  toggleSearch = () => {
    if (!Build.isBrowser) return;
    // Only work on desktop
    if (window.innerWidth <= 960) {
      return;
    }

    const newVisibility = !this.isVisible;
    this.isVisible = newVisibility;
    this.opened = newVisibility;
    this.updateSearchVisibilityClass();

    // Emit change event
    this.snSearchOpenChange.emit({ opened: newVisibility });

    // Focus management with delay
    if (newVisibility) {
      // Focus after opening
      if (this.focusTimeout) clearTimeout(this.focusTimeout);
      this.focusTimeout = window.setTimeout(() => {
        this.inputElement?.focus();
      }, 80);
    } else {
      // Blur after closing
      if (this.blurTimeout) clearTimeout(this.blurTimeout);
      this.blurTimeout = window.setTimeout(() => {
        this.inputElement?.blur();
      }, 80);
    }
  };

  /**
   * Filter suggestions based on search value
   */
  private getFilteredSuggestions() {
    if (!this.searchValue || this.searchValue.trim() === '') {
      return this.suggestions;
    }

    const searchLower = this.searchValue.toLowerCase().trim();
    return this.suggestions.filter(item => {
      const label = typeof item === 'string' ? item : item.label;
      return label.toLowerCase().includes(searchLower);
    });
  }

  /**
   * Handle input change
   */
  handleInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.searchValue = target.value;

    // Reset selected index when input changes
    this.selectedSuggestionIndex = -1;

    // Clear error when typing
    if (this.hasError) {
      this.hasError = false;
    }

    // Emit input change event
    this.snSearchInputChange.emit({ value: this.searchValue });

    // Get filtered suggestions
    const filteredSuggestions = this.getFilteredSuggestions();

    // Show suggestions if empty or if there are filtered results
    if (this.searchValue === '') {
      this.showSuggestions = true;
      this.showControls = false;
      requestAnimationFrame(() => {
        this.handleWindowResize();
      });
    } else if (filteredSuggestions.length > 0) {
      // Show suggestions if there are matches
      this.showSuggestions = true;
      this.showControls = false;
      requestAnimationFrame(() => {
        this.handleWindowResize();
      });
    } else {
      // Hide suggestions if no matches
      this.showSuggestions = false;
      if (this.searchValue.length > 2) {
        // Show controls when more than 2 characters and no matches
        this.showControls = true;
      } else {
        // Hide controls when 1-2 characters
        this.showControls = false;
      }
    }

    this.updateAriaAttributes();
  };

  /**
   * Handle input key down (navigation and selection)
   */
  handleKeyDown = (event: KeyboardEvent) => {
    const filteredSuggestions = this.getFilteredSuggestions();

    // Only handle navigation when suggestions are visible and there are filtered results
    if (!this.showSuggestions || filteredSuggestions.length === 0) {
      // If Enter pressed and no suggestions visible, handle submit
      if (event.key === 'Enter' && this.searchValue.length > 0) {
        this.handleSearchSubmit();
      }
      // If Escape pressed and suggestions are hidden, close the entire search
      if (event.key === 'Escape') {
        event.preventDefault();
        this.isVisible = false;
        this.opened = false;
        this.updateSearchVisibilityClass();
        this.snSearchOpenChange.emit({ opened: false });
        this.inputElement?.blur();
      }
      return;
    }

    // Prevent double processing
    if (this.isProcessingKeyDown) return;

    // Only handle arrow keys, Enter, Tab, Escape, Home, and End when suggestions are visible
    if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape', 'Home', 'End'].includes(event.key)) {
      return;
    }

    this.isProcessingKeyDown = true;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const currentIndex = this.selectedSuggestionIndex;
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowDown':
        // If no item is selected (index -1), select first item (index 0)
        // If at last item, wrap to first item (circular navigation)
        if (currentIndex < 0) {
          newIndex = 0;
        } else if (currentIndex >= filteredSuggestions.length - 1) {
          newIndex = 0; // Wrap to first item
        } else {
          newIndex = currentIndex + 1;
        }
        break;
      case 'ArrowUp':
        // If no item is selected (index -1), select last item
        // If at first item (index 0), wrap to last item (circular navigation)
        if (currentIndex < 0) {
          newIndex = filteredSuggestions.length - 1;
        } else if (currentIndex === 0) {
          newIndex = filteredSuggestions.length - 1; // Wrap to last item
        } else {
          newIndex = currentIndex - 1;
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (currentIndex >= 0 && currentIndex < filteredSuggestions.length) {
          this.selectSuggestion(currentIndex);
          this.isProcessingKeyDown = false;
          return;
        }
        // If no item selected but suggestions visible, close suggestions
        if (this.showSuggestions) {
          this.showSuggestions = false;
          this.selectedSuggestionIndex = -1;
          this.updateAriaAttributes();
        }
        break;
      case 'Tab':
        if (currentIndex >= 0 && currentIndex < filteredSuggestions.length) {
          this.selectSuggestion(currentIndex);
          this.isProcessingKeyDown = false;
          return;
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.showSuggestions = false;
        newIndex = -1;
        this.updateAriaAttributes();
        break;
      case 'Home':
        if (filteredSuggestions.length > 0) {
          event.preventDefault();
          newIndex = 0;
        }
        break;
      case 'End':
        if (filteredSuggestions.length > 0) {
          event.preventDefault();
          newIndex = filteredSuggestions.length - 1;
        }
        break;
    }

    // Update selected index only if it changed
    if (newIndex !== currentIndex) {
      this.selectedSuggestionIndex = newIndex;
      this.scrollToSelectedSuggestion();
      this.updateAriaAttributes();
    }

    // Reset flag after a short delay to allow next keypress
    setTimeout(() => {
      this.isProcessingKeyDown = false;
    }, 0);
  };

  /**
   * Handle input key up (Enter to submit when no suggestions)
   */
  handleKeyUp = (event: KeyboardEvent) => {
    // Only handle Enter if suggestions are not visible or empty
    if (event.key === 'Enter' && (!this.showSuggestions || this.searchValue !== '')) {
      if (this.searchValue.length > 0) {
        this.handleSearchSubmit();
      } else {
        this.hasError = true;
      }
    }
  };

  /**
   * Handle search submit
   */
  private handleSearchSubmit = () => {
    this.snSearchSubmit.emit({ value: this.searchValue });

    // Determine base URL
    let baseUrl = window.location.origin;
    if (baseUrl.includes('localhost:3333')) {
      baseUrl = 'https://mark8.signnow-dev.xyz';
    }

    // Redirect
    window.location.href = `${baseUrl}/search?q=${encodeURIComponent(this.searchValue)}`;
  };

  /**
   * Select suggestion by index
   */
  private selectSuggestion = (index: number) => {
    const filteredSuggestions = this.getFilteredSuggestions();
    if (index < 0 || index >= filteredSuggestions.length) return;

    const item = filteredSuggestions[index];
    const href = typeof item === 'string' ? '#' : item.href || '#';

    if (Build.isBrowser && href && href !== '#') {
      window.location.href = href;
    }
  };

  /**
   * Scroll to selected suggestion to keep it visible
   */
  private scrollToSelectedSuggestion = () => {
    if (!this.suggestionsListElement || this.selectedSuggestionIndex < 0) return;

    requestAnimationFrame(() => {
      if (!this.suggestionsListElement) return;

      const selectedElement = this.suggestionsListElement.querySelector(`li:nth-child(${this.selectedSuggestionIndex + 1})`) as HTMLElement;

      if (selectedElement) {
        const container = this.suggestionsListElement;
        const containerRect = container.getBoundingClientRect();
        const elementRect = selectedElement.getBoundingClientRect();

        // Check if element is above visible area
        if (elementRect.top < containerRect.top) {
          container.scrollTop = selectedElement.offsetTop;
        }
        // Check if element is below visible area
        else if (elementRect.bottom > containerRect.bottom) {
          container.scrollTop = selectedElement.offsetTop - container.clientHeight + selectedElement.offsetHeight;
        }
      }
    });
  };

  /**
   * Update ARIA attributes for accessibility
   */
  private updateAriaAttributes = () => {
    if (!this.inputElement) return;

    this.inputElement.setAttribute('role', 'combobox');
    this.inputElement.setAttribute('aria-expanded', this.showSuggestions ? 'true' : 'false');
    this.inputElement.setAttribute('aria-autocomplete', 'list');
    this.inputElement.setAttribute('aria-haspopup', 'listbox');

    if (this.showSuggestions && this.suggestionsListElement) {
      this.inputElement.setAttribute('aria-controls', 'search-suggestions-listbox');
      if (this.selectedSuggestionIndex >= 0) {
        this.inputElement.setAttribute('aria-activedescendant', `suggestion-option-${this.selectedSuggestionIndex}`);
      } else {
        this.inputElement.removeAttribute('aria-activedescendant');
      }
    } else {
      this.inputElement.removeAttribute('aria-controls');
      this.inputElement.removeAttribute('aria-activedescendant');
    }
  };

  /**
   * Handle input focus
   */
  handleFocus = () => {
    const filteredSuggestions = this.getFilteredSuggestions();

    // Show suggestions on focus if input is empty or if there are filtered results
    if (this.searchValue === '' || filteredSuggestions.length > 0) {
      this.showSuggestions = true;
      // Update max-height when showing suggestions
      requestAnimationFrame(() => {
        this.handleWindowResize();
      });
    }

    // Show controls if value is long enough and no suggestions match
    if (this.searchValue.length > 2 && filteredSuggestions.length === 0) {
      this.showControls = true;
    } else {
      this.showControls = false;
    }

    // Reset selected index on focus
    this.selectedSuggestionIndex = -1;
    this.updateAriaAttributes();

    // Add suggestions-active class to mobile menu
    if (Build.isBrowser) {
      const mobileContent = document.querySelector('.navbar-type-1__mobile-content');
      if (mobileContent) {
        mobileContent.classList.add('suggestions-active');
      }
    }
  };

  /**
   * Handle input blur
   */
  handleBlur = () => {
    // Hide controls on blur (suggestions will be hidden by document click)
    this.showControls = false;

    // Remove suggestions-active class from mobile menu
    if (Build.isBrowser) {
      const mobileContent = document.querySelector('.navbar-type-1__mobile-content');
      if (mobileContent) {
        mobileContent.classList.remove('suggestions-active');
      }
    }
  };

  /**
   * Clear search input and hide search
   */
  clearSearch = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    this.searchValue = '';
    this.isVisible = false;
    this.opened = false;
    this.updateSearchVisibilityClass();
    this.showControls = false;
    this.showSuggestions = false;
    this.selectedSuggestionIndex = -1;
    this.hasError = false;

    // Emit clear event
    this.snSearchClear.emit({});
    this.snSearchOpenChange.emit({ opened: false });
    this.updateAriaAttributes();
  };

  /**
   * Handle clicks outside to hide suggestions
   */
  handleDocumentClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    // Check if click is outside the search wrapper
    if (this.searchWrapperElement && !this.searchWrapperElement.contains(target)) {
      this.showSuggestions = false;
      this.selectedSuggestionIndex = -1;
      this.updateAriaAttributes();
    }
  };

  /**
   * Stop propagation for clicks inside search
   */
  handleSearchClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  render() {
    // Render only on client-side to reduce SSR payload and avoid hydration issues
    if (!Build.isBrowser) {
      return <Host data-color-mode={this.colorMode}></Host>;
    }

    const wrapperClasses = {
      'sn-navbar-search': true,
      'me-container': true,
      'is-visible': this.isVisible,
      'has-error': this.hasError,
      'has-suggestions': this.showSuggestions && this.getFilteredSuggestions().length > 0,
    };

    const filteredSuggestions = this.getFilteredSuggestions();
    const suggestionsClasses = {
      'suggestions-list': true,
      'me-container': true,
      'is-visible': this.showSuggestions && filteredSuggestions.length > 0,
    };

    const controlsClasses = {
      'search-controls': true,
      'is-visible': this.showControls,
    };

    return (
      <Host data-color-mode={this.colorMode}>
      <div
        class={wrapperClasses}
        ref={el => (this.searchWrapperElement = el)}
        onClick={this.handleSearchClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            this.handleSearchClick(e as any);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <label class="sn-navbar-search__field">
          <span class="accessibility-hidden-content">{this.searchLabel}</span>
          <SearchIcon />
          <input
            ref={el => {
              this.inputElement = el as HTMLInputElement;
              if (el) {
                this.updateAriaAttributes();
              }
            }}
            class="sn-navbar-search__input"
            type="text"
            placeholder={this.placeholder}
            value={this.searchValue}
            onInput={this.handleInputChange}
            onKeyDown={this.handleKeyDown}
            onKeyUp={this.handleKeyUp}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
          />
          <CloseIcon onClick={this.clearSearch} />
        </label>

        {this.hasError && <div class="search-error">{this.searchErrorLabel}</div>}

        <div class={controlsClasses}>
          <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10 1.90855V8.01831H3.41421L4.70711 6.70176C5.09763 6.3041 5.09763 5.65935 4.70711 5.26168C4.31658 4.86401 3.68342 4.86401 3.29289 5.26168L0.292893 8.31656C0.197017 8.41419 0.124679 8.52671 0.075879 8.64681C0.0269856 8.76688 0 8.89853 0 9.0366C0 9.31441 0.109253 9.56624 0.286395 9.74996C0.288879 9.75254 0.291377 9.7551 0.293888 9.75765L3.29289 12.8115C3.68342 13.2092 4.31658 13.2092 4.70711 12.8115C5.09763 12.4139 5.09763 11.7691 4.70711 11.3714L3.41421 10.0549H11C11.5523 10.0549 12 9.59899 12 9.0366V1.90855C12 1.34616 11.5523 0.890259 11 0.890259C10.4477 0.890259 10 1.34616 10 1.90855Z"
              fill="#667988"
            />
          </svg>
          <span>{this.startSearchingLabel}</span>
        </div>

        <ul ref={el => (this.suggestionsListElement = el)} class={suggestionsClasses} role="listbox" id="search-suggestions-listbox" aria-label="Search suggestions">
          {filteredSuggestions.map((item, index) => {
            const label = typeof item === 'string' ? item : item.label;
            const href = typeof item === 'string' ? '#' : item.href || '#';
            const optionId = `suggestion-option-${index}`;
            const isSelected = index === this.selectedSuggestionIndex;

            return (
              <li
                key={optionId}
                id={optionId}
                role="option"
                aria-selected={isSelected ? 'true' : 'false'}
                tabIndex={this.showSuggestions && isSelected ? 0 : -1}
                class={{
                  'suggestions-item': true,
                  'suggestions-item--selected': isSelected,
                  'suggestions-item--focused': isSelected,
                }}
                onMouseEnter={() => {
                  this.selectedSuggestionIndex = index;
                  this.updateAriaAttributes();
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    this.selectSuggestion(index);
                  }
                }}
              >
                <a class="suggestions-link" href={href}>
                  {label}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
      </Host>
    );
  }
}
